import type { ColumnDef } from '@tanstack/react-table'
import { Download, Filter, Receipt } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  type AgentValueSummary,
  getValueReceipts,
  type ValueReceiptsResponse,
} from '@/features/agents/services/valueReceiptsService'
import {
  type BillingCycle,
  BillingCycleSelector,
} from '@/features/billing/components/BillingCycleSelector'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import type { Customer } from '../../services/customerService'

interface SignalTransaction {
  name: string
  type: 'Activity' | 'Outcome' | 'Task'
  count: number
  cost: number
  fte_value: number
  total: number
  agent_name: string
}

interface ValueReceiptsTabProps {
  customer: Customer
}

function billingCycleToDateRange(cycle: BillingCycle): {
  from_date: string
  to_date: string
} {
  const now = new Date()
  const toDate = now.toISOString()

  const ago = (days: number) =>
    new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString()

  switch (cycle) {
    case 'last_1h':
      return {
        from_date: new Date(now.getTime() - 60 * 60 * 1000).toISOString(),
        to_date: toDate,
      }
    case 'today': {
      const start = new Date(now)
      start.setHours(0, 0, 0, 0)
      return { from_date: start.toISOString(), to_date: toDate }
    }
    case 'yesterday': {
      const start = new Date(now)
      start.setDate(start.getDate() - 1)
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setHours(23, 59, 59, 999)
      return { from_date: start.toISOString(), to_date: end.toISOString() }
    }
    case 'last_24h':
      return { from_date: ago(1), to_date: toDate }
    case 'last_7_days':
      return { from_date: ago(7), to_date: toDate }
    case 'last_14d':
      return { from_date: ago(14), to_date: toDate }
    case 'last_30_days':
      return { from_date: ago(30), to_date: toDate }
    case 'last_90d':
      return { from_date: ago(90), to_date: toDate }
    case 'last_180d':
      return { from_date: ago(180), to_date: toDate }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      return { from_date: start.toISOString(), to_date: toDate }
    }
    case 'year_to_date': {
      const start = new Date(now.getFullYear(), 0, 1)
      return { from_date: start.toISOString(), to_date: toDate }
    }
    case 'all_time':
      return { from_date: new Date(0).toISOString(), to_date: toDate }
    // current / previous / custom_range → default to last 30 days
    default:
      return { from_date: ago(30), to_date: toDate }
  }
}

function mapActionType(
  type: 'task' | 'workflow' | 'outcome'
): 'Task' | 'Activity' | 'Outcome' {
  if (type === 'task') {
    return 'Task'
  }
  if (type === 'workflow') {
    return 'Activity'
  }
  return 'Outcome'
}

export function ValueReceiptsTab({ customer }: ValueReceiptsTabProps) {
  const { currentEnvironment } = useEnvironment()
  const [transactions, setTransactions] = useState<SignalTransaction[]>([])
  const [agentSummaries, setAgentSummaries] = useState<AgentValueSummary[]>([])
  const [totalSummary, setTotalSummary] = useState<
    ValueReceiptsResponse['summary'] | null
  >(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<BillingCycle>('current')

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  useEffect(() => {
    const { from_date, to_date } = billingCycleToDateRange(period)

    setLoading(true)
    getValueReceipts({ from_date, to_date, customer_id: customer.id })
      .then(data => {
        setTransactions(
          data.actions.map(a => ({
            name: a.action_name,
            type: mapActionType(a.action_type),
            count: a.event_count,
            cost: 0,
            fte_value: a.fte_value,
            total: a.total_value,
            agent_name: a.agent_name,
          }))
        )
        setAgentSummaries(data.agents)
        setTotalSummary(data.summary)
      })
      .catch(error => {
        toast.error(extractErrorMessage(error))
      })
      .finally(() => setLoading(false))
  }, [period, customer.id, currentEnvironment?.id])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Define table columns
  const transactionColumns: ColumnDef<SignalTransaction>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      cell: ({ row }) => (
        <span className='font-medium'>{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 120,
      cell: ({ row }) => (
        <Badge
          variant='outline'
          className={`${
            row.original.type === 'Activity'
              ? 'bg-blue-50 text-blue-700 border-blue-300'
              : row.original.type === 'Outcome'
                ? 'bg-green-50 text-green-700 border-green-300'
                : 'bg-purple-50 text-purple-700 border-purple-300'
          }`}
        >
          {row.original.type}
        </Badge>
      ),
    },
    {
      accessorKey: 'count',
      header: 'Count',
      size: 100,
      cell: ({ row }) => (
        <span className='font-mono'>{row.original.count.toLocaleString()}</span>
      ),
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
      size: 100,
      cell: ({ row }) => (
        <span className='font-mono'>${row.original.cost}</span>
      ),
    },
    {
      accessorKey: 'fte_value',
      header: 'FTE value',
      size: 120,
      cell: ({ row }) => (
        <span className='font-mono'>
          {formatCurrency(row.original.fte_value)}
        </span>
      ),
    },
    {
      accessorKey: 'total',
      header: 'Total',
      size: 120,
      cell: ({ row }) => (
        <span className='font-mono font-medium'>
          {formatCurrency(row.original.total)}
        </span>
      ),
    },
  ]

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header Skeleton */}
        <div className='flex justify-between items-center'>
          <div className='animate-pulse'>
            <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-48 mb-2'></div>
            <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-80'></div>
          </div>
          <div className='animate-pulse'>
            <div className='h-10 bg-slate-300 dark:bg-slate-600 rounded w-32'></div>
          </div>
        </div>

        {/* Cards Skeleton */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Card key={i} className='bg-transparent border'>
              <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-20'></div>
                </div>
                <div className='animate-pulse'>
                  <div className='h-4 w-4 bg-slate-300 dark:bg-slate-600 rounded'></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className='animate-pulse'>
                  <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-24 mb-1'></div>
                  <div className='h-3 bg-slate-300 dark:bg-slate-600 rounded w-16'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Agent Cards Skeleton */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
          {Array.from({ length: 3 }).map((_, i) => (
            <Card
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              key={i}
              className='bg-transparent border h-24 flex items-center'
            >
              <CardContent className='px-3 py-0 w-full'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-24 mb-1'></div>
                  <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-20'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table Skeleton */}
        <Card className='bg-transparent border'>
          <CardHeader>
            <div className='animate-pulse'>
              <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-32 mb-2'></div>
              <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-48'></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='animate-pulse space-y-4'>
              <div className='grid grid-cols-7 gap-4 pb-4 border-b'>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={i}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  ></div>
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <div key={i} className='grid grid-cols-7 gap-4 py-3'>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={j}
                      className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                    ></div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-bold'>Value Receipts</h2>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm'>
            <Download className='w-4 h-4 mr-2' />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Billing Period Filter */}
      <div className='flex justify-start'>
        <BillingCycleSelector
          value={period}
          onValueChange={setPeriod}
          className='w-48'
        />
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>FTE Value</span>
              <span className='text-md font-bold text-red-600'>
                {formatCurrency(totalSummary?.total_fte_value ?? 0)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Signals</span>
              <span className='text-md font-bold'>
                {(totalSummary?.total_events ?? 0).toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Value</span>
              <span className='text-md font-bold text-green-600'>
                {formatCurrency(totalSummary?.total_value ?? 0)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Cards */}
      <h3 className='text-lg font-medium mb-2 text-slate-500'>Agents</h3>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
        {agentSummaries.length === 0 ? (
          <p className='text-sm text-muted-foreground col-span-3'>
            No agent data for this period.
          </p>
        ) : (
          agentSummaries.map(agent => (
            <Card key={agent.agent_id} className='bg-transparent border'>
              <CardHeader>
                <CardTitle>{agent.agent_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  {/* Agent Summary */}
                  <div className='grid grid-cols-3 gap-2'>
                    <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-center'>
                      <div className='text-md font-bold text-slate-700 dark:text-slate-300'>
                        {formatCurrency(agent.fte_value)}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        FTE Value
                      </div>
                    </div>

                    <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-center'>
                      <div className='text-md font-bold text-slate-600 dark:text-slate-400'>
                        $0
                      </div>
                      <div className='text-xs text-muted-foreground'>Cost</div>
                    </div>

                    <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-center'>
                      <div className='text-md font-bold text-slate-800 dark:text-slate-200'>
                        {formatCurrency(agent.total_value)}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        Total Value
                      </div>
                    </div>
                  </div>

                  {/* Signal Breakdown */}
                  <div>
                    <h4 className='font-medium mb-3'>Activity Breakdown</h4>
                    <div className='space-y-2'>
                      {agent.actions.map(action => (
                        <div
                          key={action.action_id}
                          className='flex items-center justify-between p-3 border rounded-lg'
                        >
                          <div className='flex items-center gap-3'>
                            <div>
                              <div className='text-md font-medium'>
                                {action.action_name}
                              </div>
                              <div className='flex items-center gap-2 mt-1'>
                                <Badge
                                  variant='outline'
                                  className={`${
                                    action.action_type === 'workflow'
                                      ? 'bg-blue-50 text-blue-700 border-blue-300'
                                      : action.action_type === 'outcome'
                                        ? 'bg-green-50 text-green-700 border-green-300'
                                        : 'bg-purple-50 text-purple-700 border-purple-300'
                                  }`}
                                >
                                  {action.action_type}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          <div className='text-right'>
                            <div className='text-md font-bold'>
                              {action.event_count.toLocaleString()}
                            </div>
                            <div className='text-xs text-muted-foreground'>
                              FTE: {formatCurrency(action.fte_value)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Signal Details */}
      <div className='bg-transparent '>
        <div className='p-0 border-b'>
          <div className='flex justify-between items-center'>
            <h3 className='text-lg font-medium text-slate-500 mb-2'>
              Signal details
            </h3>
            <div className='flex items-center gap-2'>
              <Button variant='outline' size='sm'>
                <Filter className='w-4 h-4' />
                Filters
              </Button>
            </div>
          </div>
        </div>

        {transactions.length > 0 ? (
          <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
            <EnhancedTable
              data={transactions}
              columns={transactionColumns.map(col => ({
                ...col,
                enableSorting: false,
              }))}
              height='400px'
              className='bg-transparent'
            />
          </div>
        ) : (
          <div className='text-center py-12 text-muted-foreground'>
            <Receipt className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <h3 className='text-lg font-medium mb-2'>No transactions found</h3>
            <p className='text-sm max-w-sm mx-auto'>
              Transaction data will appear here once signals are processed.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
